import { useEffect, useState } from 'react';
import type { LegacyChatResult } from '@/types/legacyChat';
import {
  formatMetric,
  getQbankData,
  getString,
  getTopicsData,
} from '@/utils/chatUtils/chatDefaultUtils';
import ExamGlobalToggle from '@/components/ora/Global/ExamGlobalToggle';
import TopicRadar from '@/components/ora/Performance/TopicRadar';
import {
  useIsSingleExamScope,
  useScopedPrimaryExam,
} from '@/components/ora/Performance/performanceScope';
import {
  fetchTopicTreeConfig,
  NURSING_TOPIC_TREE_VIEWS,
  type TopicTreeView,
} from '@/services/topics';
import { isNclexExam } from '@/utils/examUtils';

type DashboardBlockBodyProps = {
  data: LegacyChatResult;
};

type QbankScore = {
  correctPercent?: number;
  totalResponses?: number;
  correct?: number;
};

export const DashboardBlockBody = ({ data }: DashboardBlockBodyProps) => {
  const qbank = getQbankData(data);
  const examScore = (qbank?.exam_score ?? null) as QbankScore | null;
  const globalScore = (qbank?.global_score ?? null) as QbankScore | null;
  const hasExamScope = Boolean(examScore);

  const [view, setView] = useState<'exam' | 'global'>(
    hasExamScope ? 'exam' : 'global'
  );

  // A single-exam (NCLEX-RN) user has no global scope. Without an exam score
  // the block shows the exam branch's empty state, never the global one.
  const isSingleExamScope = useIsSingleExamScope();
  const effectiveView = isSingleExamScope ? 'exam' : view;
  // Match the topic set the /performance radar shows: it renders the 'systems'
  // view, falling back to the exam's default view when systems isn't allowed.
  // Without a topicView the radar fetches a more granular all-topics set.
  // A nursing exam has no systems view, so it starts on its own first view.
  const primaryExam = useScopedPrimaryExam();

  const fallbackTopicView: TopicTreeView = isNclexExam(primaryExam)
    ? NURSING_TOPIC_TREE_VIEWS[0]
    : 'systems';

  const [topicView, setTopicView] = useState<TopicTreeView>(fallbackTopicView);

  useEffect(() => {
    if (effectiveView !== 'exam' || !primaryExam?.id) {
      setTopicView(fallbackTopicView);
      return;
    }
    let active = true;

    fetchTopicTreeConfig({ examId: primaryExam.id })
      .then((config) => {
        if (!active) return;

        setTopicView(
          config.allowedViews.includes('systems')
            ? 'systems'
            : config.defaultView
        );
      })
      .catch(() => {
        if (active) setTopicView(fallbackTopicView);
      });

    return () => {
      active = false;
    };
  }, [effectiveView, primaryExam?.id, fallbackTopicView]);

  if (!data.user_data) {
    return <div className="chat-ui_missing">User data was not available.</div>;
  }

  const isExam = effectiveView === 'exam';
  const score = isExam ? examScore : globalScore;

  const accuracy =
    score?.correctPercent ?? (isExam ? undefined : qbank?.overall_accuracy);

  const completed =
    score?.totalResponses ?? (isExam ? undefined : qbank?.overall_total);

  const correct = score?.correct;

  const weakTopics = getTopicsData(
    data,
    isExam ? 'weak_topics' : 'weak_topics_global'
  ).slice(0, 5);

  const strongTopics = getTopicsData(
    data,
    isExam ? 'strong_topics' : 'strong_topics_global'
  ).slice(0, 5);

  const examName = getString(data.user_data.active_exam);

  // ORA-1101: the server ranked this scope's topics by readiness.
  const isReadinessRanked =
    data.user_data[isExam ? 'topic_metric' : 'topic_metric_global'] ===
    'readiness';

  return (
    <div className="chat-ui_dashboard">
      <div className="chat-ui_dashboard_header">
        <div className="chat-ui_dashboard_scope">
          <span>Performance</span>
          <strong className="block mt-1">
            {isExam ? examName || 'Your Exam' : 'All Exams'}
          </strong>
        </div>
        <ExamGlobalToggle value={effectiveView} onChange={setView} />
      </div>

      <div className="chat-ui_dashboard_body">
        <div className="chat-ui_dashboard_charts">
          <div className="chat-ui_stats">
            <div>
              <span>QBank</span>
              <strong className="text-lg">{formatMetric(accuracy, '%')}</strong>
              <small>accuracy</small>
            </div>
            {!isExam ? (
              <div>
                <span>Recent</span>
                <strong className="text-lg">
                  {formatMetric(qbank?.recent_accuracy, '%')}
                </strong>
                <small>{formatMetric(qbank?.recent_total)} questions</small>
              </div>
            ) : null}
            <div>
              <span>Correct</span>
              <strong className="text-lg">{formatMetric(correct)}</strong>
              <small>questions</small>
            </div>
            <div>
              <span>Completed</span>
              <strong className="text-lg">{formatMetric(completed)}</strong>
              <small>questions</small>
            </div>
          </div>

          <div className="chat-ui_topic-grid">
            <section>
              <h4>Weakest Topics{isExam ? '' : ' (All Exams)'}</h4>
              <TopicList
                topics={weakTopics}
                isReadinessRanked={isReadinessRanked}
              />
            </section>
            <section>
              <h4>Strongest Topics{isExam ? '' : ' (All Exams)'}</h4>
              <TopicList
                topics={strongTopics}
                isReadinessRanked={isReadinessRanked}
              />
            </section>
          </div>
          {isReadinessRanked ? (
            <p className="text-xs text-fg-secondary">
              Ranked by readiness (0–100), not percent correct.
            </p>
          ) : null}
        </div>

        <div className="chat-ui_dashboard_radar">
          <TopicRadar
            view={effectiveView}
            topicView={topicView}
            showToggle={false}
          />
        </div>
      </div>
    </div>
  );
};

const TopicList = ({
  topics,
  isReadinessRanked = false,
}: {
  topics: Array<Record<string, unknown>>;
  /** Show each topic's readiness (0–100, no %) instead of its accuracy. */
  isReadinessRanked?: boolean;
}) => {
  if (!topics.length) return <p>No topic data available.</p>;

  return (
    <ul>
      {topics.map((topic, index) => (
        <li key={`${topic.name || topic.topic || index}`}>
          <span>{getString(topic.name) || getString(topic.topic) || '?'}</span>
          <strong>
            {isReadinessRanked
              ? `Readiness ${formatMetric(topic.readiness)}`
              : formatMetric(topic.accuracy ?? topic.percent_correct, '%')}
          </strong>
        </li>
      ))}
    </ul>
  );
};
